import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@naf-simba/elements-buttons';
import { ArticleHeader } from '@naf-simba/sections-headers';
import type { SanityBlock } from '@sanity/client';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { HeroImages } from '@components/image/HeroImages';
import { mapSimbaVariant } from '@utils/simba';
import { InternalLinkType } from '@appTypes/internalLinkType';
import { CloudinaryImageType } from '@appTypes/CloudinaryImageType';

export interface HeroWithTitleAndWideImageType {
  heroVariant: 'heroWithTitleAndWideImage' | 'heroWithTitleAndWideAndShortImage';
  header: string;
  image: CloudinaryImageType;
  mobileImage?: CloudinaryImageType;
  body?: SanityBlock[];
  ingress?: string;
  ctaButton?: {
    buttonText: string;
    data: InternalLinkType;
  };
  ctaInternalLink?: {
    text?: string;
    referenceComponent?: InternalLinkType;
  };
  ctaExternalLink?: {
    text?: string;
    link?: string;
    type: string;
    blank?: boolean;
  };
  overlay?: boolean;
  halfScreen?: boolean;
}

export const HeroWithTitleAndWideImage = ({
  hero: { header, ctaInternalLink, image, mobileImage, ingress, overlay },
}: {
  hero: HeroWithTitleAndWideImageType;
}) => {
  const navigate = useNavigate();
  const ctaUrl = useDocumentUrlWithNode(ctaInternalLink?.referenceComponent);
  const ctaButtonVariant = mapSimbaVariant('secondary');
  const linkButton =
    ctaInternalLink && ctaUrl ? (
      <Button
        variant={ctaButtonVariant.variant}
        color={ctaButtonVariant.color}
        shade={ctaButtonVariant.shade}
        onClick={() => {
          if (ctaUrl) {
            navigate(ctaUrl);
          }
        }}
      >
        {ctaInternalLink.text}
      </Button>
    ) : undefined;

  if (image?.publicId) {
    return (
      <ArticleHeader
        imageOrVideo={
          <HeroImages
            image={image}
            mobileImage={mobileImage}
            cropOnSmallScreens
            alt={image.alt || image.altText || image.caption || undefined}
          />
        }
        ingress={ingress}
        title={header}
        ctaButton={linkButton}
        headingVariant="display"
        height="full-viewport"
        wideGrid
        nafBranded={false}
        overlay={overlay}
      />
    );
  }
  return (
    <ArticleHeader
      ingress={ingress}
      title={header}
      headingVariant="display"
      height="full-viewport"
      wideGrid
      nafBranded={false}
    />
  );
};
